import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    ViewEncapsulation,
} from '@angular/core'

import type { ClassValue } from 'clsx'

import { mergeClasses } from '@/styles/utils/merge-classes'

import { ZardDropdownMenuComponent } from './dropdown.component'
import { ZardDropdownService } from './dropdown.service'
import { dropdownItemVariants, type ZardDropdownItemVariants } from './dropdown.variants'

@Component({
    selector: 'z-dropdown-menu-item, [z-dropdown-menu-item]',
    template: ` <ng-content /> `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class]': 'classes()',
        '[attr.data-disabled]': 'disabled() || null',
        '[attr.data-selected]': 'selected() || null',
        '[attr.data-variant]': 'variant()',
        '[attr.data-inset]': 'inset() || null',
        '[attr.aria-disabled]': 'disabled()',
        '(click.prevent-with-stop)': 'onClick()',
        role: 'menuitem',
        tabindex: '-1',
    },
    exportAs: 'zDropdownMenuItem',
})
export class ZardDropdownMenuItemComponent {
    // Prefer closing the nearest dropdown menu component (when used within <z-dropdown-menu>)
    // Falls back to service-based dropdowns when not present.
    private readonly dropdownMenu = inject(ZardDropdownMenuComponent, {
        optional: true,
        skipSelf: true,
    })
    private readonly dropdownService = inject(ZardDropdownService)

    readonly variant = input<ZardDropdownItemVariants['variant']>('default')
    readonly inset = input(false, { transform: booleanAttribute })
    readonly disabled = input(false, { transform: booleanAttribute })
    readonly selected = input(false, { transform: booleanAttribute })
    readonly class = input<ClassValue>('')

    onClick() {
        if (this.disabled()) {
            return
        }

        // Close dropdown after click (supports both component-managed and service-managed dropdowns)
        setTimeout(() => {
            if (this.dropdownMenu) {
                this.dropdownMenu.close()
                return
            }
            this.dropdownService.close()
        }, 0)
    }

    protected readonly classes = computed(() =>
        mergeClasses(
            dropdownItemVariants({
                variant: this.variant(),
                inset: this.inset(),
            }),
            this.class(),
        ),
    )
}
